/**
 * Likely error patterns grouped by category.
 * Each category has a label for display and an array of regex source strings.
 * 
 * No matter how many patterns are added, the performance will not be affected.
 * Performance is O(n) where n is the length of the HTML string.
 * 
 * But the more category, the more time it takes to match.
 */
const PATTERN_CATEGORIES = [
    // 1. Captcha / human verification
    {
      type: 'captcha',
      label: 'Baseline contains captcha',
      patterns: [
        'captcha',
        'recaptcha',
        'hcaptcha',
        '_pxCaptcha',
        'verify you are (a )?human',
        "prove you'?re not a robot",
        'please complete the security check',
        "I'?m not a robot",
        'I am not a robot',
        'select all images with',
        'select all squares with',
        'click on all images that',
        'enter the characters you see',
        'enter the characters shown',
        'enter the text you see',
        'type the characters you see',
        'security check required',
        'unusual traffic from your computer network',
        'our systems have detected unusual traffic',
        'g-recaptcha',
        'data-sitekey',
        'cf-turnstile',
        'turnstile'
      ]
    },
  
    // 2. Generic access denied / WAF / rate limit
    {
      type: 'waf_generic',
      label: 'WAF or security block detected',
      patterns: [
        'access denied',
        'request blocked',
        'this request has been blocked',
        'your request has been blocked',
        'you don\'t have permission to access',
        'you do not have permission to access',
        'you are not authorized to access this resource',
        'you are not authorized to view this page',
        'this page is unavailable due to security policy',
        'this page has been blocked',
        'blocked due to suspicious activity',
        'blocked due to abuse',
        '403 forbidden',
        '403 error',
        'http 403',
        'error 403',
        '429 too many requests',
        'too many requests',
        'rate limit exceeded',
        'rate limited',
        'temporarily blocked',
        'temporarily unavailable due to unusual activity',
        'this request has been blocked by security rules',
        'this request has been blocked for security reasons',
        'blocked by security rules'
      ]
    },
  
    // 3. Cloudflare
    {
      type: 'cloudflare',
      label: 'Cloudflare protection detected',
      patterns: [
        'Attention Required!',
        'checking your browser before accessing',
        'checking your browser before accessing this site',
        'checking if (the )?site connection is secure',
        'checking if your connection is secure',
        'please stand by, while we are checking your browser',
        'verifying your browser, please wait',
        'verifying you are human',
        'verify you are human',
        'check this box to show you\'re human',
        'cf-browser-verification',
        'cloudflare ray id[:\\s]*[a-f0-9]+',
        'ray id[:\\s]*[a-f0-9]+',
        'ddos protection by cloudflare',
        'performance & security by cloudflare',
        'Just a moment'
      ]
    },
  
    // 4. Akamai
    {
      type: 'akamai',
      label: 'Akamai bot protection detected',
      patterns: [
        'pardon our interruption',
        'pardon the interruption',
        'this request was blocked by the security rules',
        'your request has been denied by the security rules',
        'your request has been denied by our security systems',
        'you don\'t have permission to access this server',
        'your support ID is[:\\s]*[\\d]+',
        'support ID[:\\s]*[\\d]+',
        'reference id[:\\s]*[\\d.]+',
        'event id[:\\s]*[\\d]+',
        'akamai',
        'bot manager'
      ]
    },
  
    // 5. AWS WAF specific
    {
      type: 'aws_waf',
      label: 'AWS WAF block detected',
      patterns: [
        'blocked by aws waf',
        'your request has been blocked by aws waf',
        'aws waf bot control',
        'bot control challenge',
        'signalnonbrowseruseragent',
        'knownbotdatacenter'
      ]
    },
  
    // 6. Azure WAF specific
    {
      type: 'azure_waf',
      label: 'Azure WAF block detected',
      patterns: [
        'request blocked by azure waf',
        'your request has been blocked by azure waf',
        'web application firewall \\(waf\\)',
        'waf rule triggered',
        'matched data:'
      ]
    },
  
    // 7. Imperva WAF specific (Incapsula)
    {
      type: 'imperva_waf',
      label: 'Imperva WAF block detected',
      patterns: [
        // Imperva block messages
        'this request was blocked by the security rules',
        'incident id[:\\s]*[\\d]+',
      ]
    },
  
    // 8. Generic bot / automation detection
    {
      type: 'bot_detection',
      label: 'Bot detection triggered',
      patterns: [
        'bot detected',
        'bot detection',
        'our systems have detected automated',
        'we have detected automated',
        'it appears you are using automated',
        'it looks like you\'re using an automated tool',
        'it looks like you are a bot',
        'it appears that you are a bot',
        'your behavior looks like a bot',
        'automated access is not allowed',
        'automated access is prohibited',
        'automated queries are not allowed',
        'no automated queries',
        'this page is protected against automated queries',
        'this page is protected against bots',
        'this site is protected by a security service',
        'this page uses security service to protect itself from online attacks',
        'please enable javascript to continue',
        'please enable javascript and cookies to continue',
        'enable javascript and cookies to continue',
        'your browser is not supported or javascript is disabled',
        'suspicious activity'
      ]
    }
  ];

/**
 * Pre-compiled combined regex for each category.
 */
const CATEGORY_PATTERNS = PATTERN_CATEGORIES.map(cat => ({
  type: cat.type,
  label: cat.label,
  regex: new RegExp(cat.patterns.join('|'), 'i')
}));

/**
 * All pattern sources flattened for individual highlighting.
 */
const ALL_PATTERN_SOURCES = PATTERN_CATEGORIES.flatMap(cat => cat.patterns);

//#region PreviewHtml component highlighting
/*
 For PreviewHtml component highlighting.

 The difference here is that these pattern match will not tell what is the problem
 But it allows individual highlighting of the patterns.
 */

/**
 * Individual regex patterns for highlighting/display purposes.
 */
export const SUSPICIOUS_PATTERNS = ALL_PATTERN_SOURCES.map(
  source => new RegExp(source, 'gi')
);

/**
 * Combined regex pattern for single-pass matching (any category).
 * Pre-compiled at module load time for performance.
 */
const COMBINED_PATTERN = new RegExp(ALL_PATTERN_SOURCES.join('|'), 'i');

/**
 * Check if HTML content contains block patterns
 * 
 * @param {string} html - The HTML content to check
 * @returns {boolean} - True if suspicious patterns are found
 */
export function checkForSuspiciousPatterns(html) {
  if (!html) return false;
  return COMBINED_PATTERN.test(html);
}
//#endregion

/**
 * Check HTML content and return detailed issue information.
 * Returns all matching categories with a combined label.
 * 
 * @param {string} html - The HTML content to check
 * @returns {{ hasIssues: boolean, types?: string[], label?: string }} - Issue details
 */
export function detectBaselineIssue(html) {

  if (!html) return { hasIssues: false };
  
  const matchedCategories = [];
  
  for (const category of CATEGORY_PATTERNS) {

    // If contains category, add it to the matched categories
    if (category.regex.test(html)) {
      matchedCategories.push({
        type: category.type,
        label: category.label
      });
    }
  }
  
  // If no categories matched, return false
  if (matchedCategories.length === 0) {
    return { hasIssues: false };
  }
  
  // If categories matched, return the categories and append every category so it shows all of them.
  return {
    hasIssues: true,
    types: matchedCategories.map(c => c.type),
    label: matchedCategories.map(c => c.label).join(', ')
  };
}

